import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const PORTRAIT =
  "https://images.unsplash.com/photo-1444312645910-ffa973656eba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 md:pt-36 pb-24">
      {/* Eyebrow + page title */}
      <section className="px-6 md:px-12 lg:px-20">
        <p className="font-eyebrow text-[11px] text-[#5C5C59]">About</p>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[110px] text-[#1A1A1A] mt-4 max-w-5xl leading-[0.95]">
          This page isn't{" "}
          <em className="editorial-italic text-[#7B8871]">about me.</em>
        </h1>
      </section>

      {/* Opening statement */}
      <section
        data-testid="about-opening"
        className="px-6 md:px-12 lg:px-20 mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10"
      >
        <div className="lg:col-span-3">
          <p className="font-eyebrow text-[10px] text-[#5C5C59]">For her</p>
        </div>
        <div className="lg:col-span-9 space-y-6">
          <p className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight max-w-4xl">
            It's about the{" "}
            <em className="editorial-italic text-[#7B8871]">version of you</em>{" "}
            that's been waiting to exhale.
          </p>
          <p className="text-[#5C5C59] text-base md:text-lg leading-relaxed max-w-2xl">
            The one who's been holding it together, showing up, and quietly
            wondering when life gets to feel like yours again.
          </p>
          <p className="text-[#1A1A1A] text-base md:text-lg leading-relaxed max-w-2xl">
            Finka was built for her.
          </p>
        </div>
      </section>

      <div className="px-6 md:px-12 lg:px-20 mt-20 md:mt-24">
        <hr className="editorial-rule" />
      </div>

      {/* Sandra intro + portrait */}
      <section
        data-testid="about-sandra"
        className="px-6 md:px-12 lg:px-20 mt-20 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-12"
      >
        <div className="lg:col-span-5">
          <img
            src={PORTRAIT}
            alt="A grounded, holistic atmosphere"
            className="w-full h-[460px] lg:h-[620px] object-cover"
          />
          <p className="font-eyebrow text-[10px] text-[#5C5C59] mt-4">
            Sandra Marie Teixeira
          </p>
        </div>
        <div className="lg:col-span-7 space-y-8">
          <p className="font-eyebrow text-[10px] text-[#5C5C59]">
            The practice
          </p>
          <p className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] leading-tight">
            I'm Sandra, a licensed clinical social worker, somatic
            practitioner, yoga teacher, and{" "}
            <em className="editorial-italic text-[#7B8871]">
              fashion psychology-informed
            </em>{" "}
            wellness guide.
          </p>
          <p className="text-[#5C5C59] text-base leading-relaxed">
            I've spent over a decade sitting with people in the tender places:
            grief, burnout, identity, the body, and the self.
          </p>
          <p className="text-[#5C5C59] text-base leading-relaxed">
            What I kept seeing was this: people didn't need more information.
            They needed permission to slow down. Space to feel at home within
            themselves. Someone to witness their becoming.
          </p>
          <p className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] leading-tight">
            That's what <em className="editorial-italic">Finka</em> is.
          </p>
          <p className="text-[#5C5C59] text-base leading-relaxed">
            A quiet practice. A space where your nervous system can soften,
            your self-expression can return, and your body can stop being
            something you manage, and start being somewhere you live.
          </p>
          <p className="text-[#5C5C59] text-base leading-relaxed">
            I know what it's like to be the one holding space for everyone else
            while quietly needing a space of your own. That is part of why I
            built this.
          </p>
          <div className="border-t border-[#D8D4CC] pt-8">
            <p className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] leading-tight">
              You don't have to have it all figured out to be here.{" "}
              <em className="editorial-italic text-[#7B8871]">
                You just have to be ready to return to yourself.
              </em>
            </p>
          </div>
          <Link
            to="/booking"
            data-testid="about-cta-book"
            className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F7F5F2] px-8 py-4 font-eyebrow text-[11px] hover:bg-[#7B8871] transition-colors duration-500 mt-4"
          >
            Reserve a session
            <ArrowUpRight
              strokeWidth={1.5}
              className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>
      </section>

      <div className="px-6 md:px-12 lg:px-20 mt-24 md:mt-28">
        <hr className="editorial-rule" />
      </div>

      {/* Credentials / Bio */}
      <section
        data-testid="about-credentials"
        className="px-6 md:px-12 lg:px-20 mt-20 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10"
      >
        <div className="lg:col-span-3">
          <p className="font-eyebrow text-[10px] text-[#5C5C59]">Bio</p>
        </div>
        <div className="lg:col-span-9 space-y-8">
          <p className="text-[#1A1A1A] text-lg md:text-xl leading-relaxed max-w-3xl">
            Sandra Marie Teixeira is a licensed clinical social worker, a
            500-hour certified yoga teacher, and a trauma and
            grief-informed practitioner. Her work bridges mental health,
            nervous system care, embodiment, identity, and self-expression.
          </p>
          <p className="text-[#5C5C59] text-base md:text-lg leading-relaxed max-w-3xl">
            She has worked in higher education, community mental health, and
            private practice, and has spoken at{" "}
            <span className="text-[#1A1A1A]">Nike HQ</span>,{" "}
            <span className="text-[#1A1A1A]">NYU</span>,{" "}
            <span className="text-[#1A1A1A]">Howard University</span>, and the{" "}
            <span className="text-[#1A1A1A]">Netherlands Embassy</span>.
          </p>

          {/* Credentials grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 border-t border-[#D8D4CC] pt-8 max-w-3xl">
            <div>
              <p className="font-eyebrow text-[10px] text-[#7B8871]">
                Clinical
              </p>
              <p className="text-[#1A1A1A] text-sm mt-2">
                Licensed Clinical Social Worker
              </p>
            </div>
            <div>
              <p className="font-eyebrow text-[10px] text-[#7B8871]">
                Movement
              </p>
              <p className="text-[#1A1A1A] text-sm mt-2">
                500-hour Certified Yoga Teacher
              </p>
            </div>
            <div>
              <p className="font-eyebrow text-[10px] text-[#7B8871]">Somatic</p>
              <p className="text-[#1A1A1A] text-sm mt-2">
                Trauma & grief-informed practitioner
              </p>
            </div>
            <div>
              <p className="font-eyebrow text-[10px] text-[#7B8871]">Style</p>
              <p className="text-[#1A1A1A] text-sm mt-2">
                Fashion psychology-informed wellness guide
              </p>
            </div>
          </div>

          {/* Speaking */}
          <div className="border-t border-[#D8D4CC] pt-8 max-w-3xl">
            <p className="font-eyebrow text-[10px] text-[#5C5C59] mb-5">
              Selected speaking
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {["Nike HQ", "NYU", "Howard University", "Netherlands Embassy"].map(
                (org) => (
                  <p
                    key={org}
                    className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] leading-tight"
                  >
                    {org}
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Closing signature */}
      <section className="px-6 md:px-12 lg:px-20 mt-24 md:mt-32">
        <hr className="editorial-rule" />
        <p className="font-eyebrow text-[10px] text-[#5C5C59] mt-8 text-center md:text-left">
          Finka · Wholistic Wellness · Est. 2026
        </p>
      </section>
    </div>
  );
}
